/**
 * Store Management — normalization and display helpers.
 */
import {
  STORE_TYPE_LABELS, BIN_TYPE_LABELS, TXN_TYPE_LABELS,
  STORE_TYPE_PREFIX, DEFAULT_ALLOWED_TRANSACTIONS,
} from '../constants/storeManagement';

export function storeRowId(row) {
  return row?.id ?? row?.store_id ?? '';
}

export function normalizeStoreList(row = {}) {
  return {
    ...row,
    id: storeRowId(row),
    store_code: row.store_code ?? '',
    store_name: row.store_name ?? '',
    store_type: row.store_type ?? '',
    parent_store_id: row.parent_store_id ?? row.parent_store?.id ?? null,
    bin_count: row.bin_count ?? 0,
    version: row.version ?? 1,
    is_active: row.is_active !== false,
    is_receiving_store: isRootStore(row),
    is_issue_store: !!row.is_issue_store,
    is_pharmacy_store: !!row.is_pharmacy_store,
    is_non_stock: !!row.is_non_stock,
  };
}

export function normalizeAllowedItemClassIds(row = {}) {
  const raw = Array.isArray(row.allowed_item_class_ids)
    ? row.allowed_item_class_ids
    : row.store_payload?.allowed_item_class_ids;
  if (!Array.isArray(raw)) return [];
  return raw.map(String).filter(Boolean);
}

export function normalizeStoreDetail(row = {}) {
  const base = normalizeStoreList(row);
  return {
    ...base,
    location_description: row.location_description ?? '',
    department_id: row.department_id ?? null,
    ward_id: row.ward_id ?? null,
    manager_user_id: row.manager_user_id ?? null,
    parent_store_id: row.parent_store_id ?? row.parent_store?.id ?? null,
    reorder_frequency_days: row.reorder_frequency_days ?? 7,
    default_reorder_level: row.default_reorder_level ?? '100.0000',
    location_payload: row.location_payload ?? {},
    store_payload: row.store_payload ?? {},
    // Prefer the top-level field; fall back to the payload key so this works
    // against a backend that hasn't shipped the response field yet.
    // [] means unrestricted — the store accepts every item class.
    allowed_item_class_ids: normalizeAllowedItemClassIds(row),
    bins: Array.isArray(row.bins) ? row.bins.map(normalizeBin) : [],
    allowed_transactions: Array.isArray(row.allowed_transactions)
      ? row.allowed_transactions.map(normalizeAllowedTxn)
      : [],
    children: Array.isArray(row.children) ? row.children.map(normalizeStoreList) : [],
  };
}

export function normalizeBin(row = {}) {
  return {
    ...row,
    id: row.id ?? row.bin_id,
    bin_code: row.bin_code ?? '',
    bin_name: row.bin_name ?? '',
    bin_type: row.bin_type ?? 'GENERAL',
    is_pickable: row.is_pickable !== false,
    is_default: !!row.is_default,
    is_active: row.is_active !== false,
  };
}

export function normalizeAllowedTxn(row = {}) {
  return {
    ...row,
    id: row.id,
    txn_type: row.txn_type ?? '',
    is_allowed: row.is_allowed !== false,
    requires_approval: !!row.requires_approval,
  };
}

/** Keep store-form transaction rows aligned with the configured template. */
export function ensureTransactionTemplate(
  txns = [],
  template = DEFAULT_ALLOWED_TRANSACTIONS,
) {
  const list = Array.isArray(txns) ? txns.filter(t => t?.txn_type) : [];
  const byType = new Map(list.map(t => [t.txn_type, t]));
  return template.map(def => {
    const existing = byType.get(def.txn_type);
    if (!existing) return { ...def };
    return {
      ...def,
      ...existing,
      txn_type: def.txn_type,
      is_allowed: existing.is_allowed !== false,
      requires_approval: !!existing.requires_approval,
    };
  });
}

export function storeTypeLabel(type) {
  return STORE_TYPE_LABELS[type] || type?.replace(/_/g, ' ') || '—';
}

export function binTypeLabel(type) {
  return BIN_TYPE_LABELS[type] || type?.replace(/_/g, ' ') || '—';
}

export function txnTypeLabel(type) {
  return TXN_TYPE_LABELS[type] || type?.replace(/_/g, ' ') || '—';
}

export function storeDisplayName(row) {
  if (!row) return '—';
  const code = row.store_code ? `${row.store_code} · ` : '';
  return `${code}${row.store_name || 'Unnamed Store'}`;
}

export function resolveParentStoreLabel(store, stores = []) {
  if (store?.parent_store?.store_name) return store.parent_store.store_name;
  const pid = store?.parent_store_id ?? store?.parent_store?.id;
  if (!pid) return '—';
  const found = stores.find(s => String(storeRowId(s)) === String(pid));
  return found ? found.store_name : '—';
}

export function toStoreListPage(raw, { page = 0, pageSize = 25 } = {}) {
  const items = Array.isArray(raw)
    ? raw
    : (raw?.items ?? raw?.data ?? []);
  const total = typeof raw?.total === 'number'
    ? raw.total
    : (typeof raw?.count === 'number' ? raw.count : items.length);
  return { items, total, page, pageSize };
}

/** Next sequential store code for a type, e.g. ST-0001 (based on existing stores). */
export function generateNextStoreCode(storeType, existingStores = []) {
  const prefix = STORE_TYPE_PREFIX[storeType] || 'ST';
  const re = new RegExp(`^${prefix}-(\\d+)$`, 'i');
  let max = 0;
  for (const s of existingStores) {
    const m = s.store_code?.trim().match(re);
    if (m) max = Math.max(max, parseInt(m[1], 10));
  }
  return `${prefix}-${String(max + 1).padStart(4, '0')}`;
}

export function isRootStore(row = {}) {
  return !row.parent_store_id && !row.parent_store?.id;
}

/**
 * True when `myStoreId` resolves to a Parent (root) store in `stores`. Used to hide
 * store-scoped actions a Parent-store user must not take (e.g. Create Requisition).
 * Permissive by design: false for unscoped users (no store) and false while `stores`
 * hasn't loaded the match yet, so gated UI never flashes hidden incorrectly.
 */
export function isRootStoreUser(myStoreId, stores = []) {
  if (!myStoreId || !Array.isArray(stores)) return false;
  const store = stores.find(s => (s.store_id || s.id) === myStoreId);
  return store ? isRootStore(store) : false;
}

/** Parent (root) stores may receive GRN; child stores may not. */
export function canReceiveGrn(store = {}) {
  return isRootStore(store) && store.is_active !== false;
}

/** GRN receiving flag derived from create/edit hierarchy role. */
export function grnAllowedForHierarchy(hierarchyRole) {
  return hierarchyRole === 'PARENT';
}

/** Keep GOODS_RECEIPT transaction rule in sync with parent/child hierarchy. */
export function applyGrnHierarchyToTransactions(txns = [], hierarchyRole) {
  const allowed = grnAllowedForHierarchy(hierarchyRole);
  return txns.map(t =>
    (t.txn_type === 'GOODS_RECEIPT' ? { ...t, is_allowed: allowed } : t),
  );
}

/** Stores eligible as parents when creating a child store (root stores only). */
export function filterEligibleParentStores(stores = []) {
  return stores.filter(isRootStore);
}

export function emptyStoreForm() {
  return {
    store_code: '',
    store_name: '',
    store_type: 'MAIN_STORE',
    hierarchy_role: 'PARENT',
    parent_store_id: '',
    location_description: '',
    department_id: '',
    ward_id: '',
    manager_user_id: '',
    is_receiving_store: true,
    is_issue_store: true,
    is_pharmacy_store: false,
    is_non_stock: false,
    reorder_frequency_days: 7,
    default_reorder_level: '100.0000',
    bins: [],
    allowed_transactions: [],
  };
}

export function emptyBinDraft() {
  return {
    bin_code: '',
    bin_name: '',
    bin_type: 'SHELF',
    aisle: '',
    rack: '',
    level: '',
    is_pickable: true,
    is_default: false,
    bin_payload: {},
  };
}
